import React from 'react';
import { Link } from 'react-router-dom';

const PrivacyPolicyPage: React.FC = () => {
  const lastUpdated = 'December 9, 2025';

  return (
    <div className="max-w-4xl mx-auto py-8 px-4">
      <div className="mb-8">
        <Link
          to="/"
          className="text-primary-600 hover:text-primary-700 dark:text-primary-400 dark:hover:text-primary-300 text-sm"
        >
          &larr; Back to Home
        </Link>
      </div>

      <article className="prose prose-secondary dark:prose-invert max-w-none">
        <h1 className="text-3xl font-bold text-secondary-900 dark:text-secondary-100 mb-2">
          Privacy Policy
        </h1>
        <p className="text-secondary-500 dark:text-secondary-400 text-sm mb-8">
          Last updated: {lastUpdated}
        </p>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-secondary-800 dark:text-secondary-200 mb-4">
            1. Introduction
          </h2>
          <p className="text-secondary-700 dark:text-secondary-300 mb-4">
            GraymatterLab ("we", "our", or "us") operates the Agent Designer
            platform (the "Service"). This Privacy Policy explains how we
            collect, use, disclose, and safeguard your information when you use
            our Service.
          </p>
          <p className="text-secondary-700 dark:text-secondary-300">
            Please read this privacy policy carefully. By using the Service, you
            agree to the collection and use of information in accordance with
            this policy.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-secondary-800 dark:text-secondary-200 mb-4">
            2. Information We Collect
          </h2>

          <h3 className="text-lg font-medium text-secondary-800 dark:text-secondary-200 mb-3">
            2.1 Personal Information
          </h3>
          <p className="text-secondary-700 dark:text-secondary-300 mb-4">
            When you create an account, we collect:
          </p>
          <ul className="list-disc list-inside text-secondary-700 dark:text-secondary-300 mb-4 space-y-1">
            <li>Email address</li>
            <li>Display name</li>
            <li>Profile information you choose to provide</li>
          </ul>

          <h3 className="text-lg font-medium text-secondary-800 dark:text-secondary-200 mb-3">
            2.2 Usage Data
          </h3>
          <p className="text-secondary-700 dark:text-secondary-300 mb-4">
            We automatically collect certain information when you use our
            Service:
          </p>
          <ul className="list-disc list-inside text-secondary-700 dark:text-secondary-300 mb-4 space-y-1">
            <li>Browser type and version</li>
            <li>Pages visited and time spent</li>
            <li>Access times and dates</li>
            <li>Device information</li>
            <li>IP address (anonymized for analytics)</li>
          </ul>

          <h3 className="text-lg font-medium text-secondary-800 dark:text-secondary-200 mb-3">
            2.3 Project Data
          </h3>
          <p className="text-secondary-700 dark:text-secondary-300">
            When you use our AI agent design tools, we store the project data
            you create, including agent specifications, workflow configurations,
            and generated outputs. This data is associated with your account and
            used to provide the Service.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-secondary-800 dark:text-secondary-200 mb-4">
            3. How We Use Your Information
          </h2>
          <p className="text-secondary-700 dark:text-secondary-300 mb-4">
            We use the information we collect to:
          </p>
          <ul className="list-disc list-inside text-secondary-700 dark:text-secondary-300 space-y-1">
            <li>Provide, maintain, and improve the Service</li>
            <li>Process and complete transactions</li>
            <li>Send you technical notices and support messages</li>
            <li>Respond to your comments and questions</li>
            <li>Analyze usage patterns to improve user experience</li>
            <li>Detect, prevent, and address technical issues</li>
            <li>Protect against unauthorized access and legal liability</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-secondary-800 dark:text-secondary-200 mb-4">
            4. Data Storage and Security
          </h2>
          <p className="text-secondary-700 dark:text-secondary-300 mb-4">
            Your data is stored on Google Cloud Platform (GCP) infrastructure
            located in the United States. We implement industry-standard
            security measures including:
          </p>
          <ul className="list-disc list-inside text-secondary-700 dark:text-secondary-300 space-y-1">
            <li>Encryption in transit (TLS/SSL) and at rest</li>
            <li>Firebase Authentication for secure user management</li>
            <li>Cloud Armor Web Application Firewall (WAF)</li>
            <li>Regular security audits and vulnerability scanning</li>
            <li>Access controls and audit logging</li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-secondary-800 dark:text-secondary-200 mb-4">
            5. Data Sharing and Disclosure
          </h2>
          <p className="text-secondary-700 dark:text-secondary-300 mb-4">
            We do not sell your personal information. We may share your
            information in the following circumstances:
          </p>
          <ul className="list-disc list-inside text-secondary-700 dark:text-secondary-300 space-y-1">
            <li>
              <strong>Service Providers:</strong> With third-party vendors who
              assist in operating our Service (e.g., Google Cloud, Firebase,
              Vertex AI)
            </li>
            <li>
              <strong>Legal Requirements:</strong> When required by law or to
              respond to legal process
            </li>
            <li>
              <strong>Safety:</strong> To protect the rights, property, or
              safety of GraymatterLab, our users, or others
            </li>
            <li>
              <strong>Business Transfers:</strong> In connection with a merger,
              acquisition, or sale of assets
            </li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-secondary-800 dark:text-secondary-200 mb-4">
            6. AI and Machine Learning
          </h2>
          <p className="text-secondary-700 dark:text-secondary-300 mb-4">
            Our Service uses Google Vertex AI (Gemini models) to power AI agent
            design features. When you interact with AI features:
          </p>
          <ul className="list-disc list-inside text-secondary-700 dark:text-secondary-300 space-y-1">
            <li>
              Your prompts and project data are sent to Google Vertex AI for
              processing
            </li>
            <li>
              AI-generated outputs are stored as part of your project data
            </li>
            <li>
              Google's data processing terms apply to AI interactions (see
              Google Cloud Terms of Service)
            </li>
          </ul>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-secondary-800 dark:text-secondary-200 mb-4">
            7. Your Rights and Choices
          </h2>
          <p className="text-secondary-700 dark:text-secondary-300 mb-4">
            You have the following rights regarding your data:
          </p>
          <ul className="list-disc list-inside text-secondary-700 dark:text-secondary-300 space-y-1">
            <li>
              <strong>Access:</strong> Request a copy of your personal data
            </li>
            <li>
              <strong>Correction:</strong> Update or correct inaccurate data
            </li>
            <li>
              <strong>Deletion:</strong> Request deletion of your account and
              associated data
            </li>
            <li>
              <strong>Export:</strong> Request an export of your project data
            </li>
          </ul>
          <p className="text-secondary-700 dark:text-secondary-300 mt-4">
            To exercise these rights, please contact us at{' '}
            <a
              href="mailto:privacy@graymatterlab.ai"
              className="text-primary-600 hover:text-primary-700 dark:text-primary-400"
            >
              privacy@graymatterlab.ai
            </a>
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-secondary-800 dark:text-secondary-200 mb-4">
            8. Data Retention
          </h2>
          <p className="text-secondary-700 dark:text-secondary-300">
            We retain your personal information for as long as your account is
            active or as needed to provide you with the Service. If you delete
            your account, we will delete your personal information within 30
            days, except where we are required to retain it for legal or
            regulatory purposes.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-secondary-800 dark:text-secondary-200 mb-4">
            9. Cookies and Tracking
          </h2>
          <p className="text-secondary-700 dark:text-secondary-300 mb-4">
            We use essential cookies for authentication and session management.
            These cookies are necessary for the Service to function and cannot
            be disabled. We do not use third-party advertising or tracking
            cookies.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-secondary-800 dark:text-secondary-200 mb-4">
            10. Children's Privacy
          </h2>
          <p className="text-secondary-700 dark:text-secondary-300">
            Our Service is not intended for children under 13 years of age. We
            do not knowingly collect personal information from children under
            13. If you believe we have collected information from a child under
            13, please contact us immediately.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-secondary-800 dark:text-secondary-200 mb-4">
            11. Changes to This Policy
          </h2>
          <p className="text-secondary-700 dark:text-secondary-300">
            We may update this Privacy Policy from time to time. We will notify
            you of any changes by posting the new Privacy Policy on this page
            and updating the "Last updated" date. You are advised to review this
            Privacy Policy periodically for any changes.
          </p>
        </section>

        <section className="mb-8">
          <h2 className="text-xl font-semibold text-secondary-800 dark:text-secondary-200 mb-4">
            12. Contact Us
          </h2>
          <p className="text-secondary-700 dark:text-secondary-300">
            If you have questions about this Privacy Policy, please contact us
            at:
          </p>
          <div className="mt-4 p-4 bg-secondary-50 dark:bg-secondary-800 rounded-lg">
            <p className="text-secondary-700 dark:text-secondary-300">
              <strong>GraymatterLab</strong>
              <br />
              Email:{' '}
              <a
                href="mailto:privacy@graymatterlab.ai"
                className="text-primary-600 hover:text-primary-700 dark:text-primary-400"
              >
                privacy@graymatterlab.ai
              </a>
            </p>
          </div>
        </section>
      </article>
    </div>
  );
};

export default PrivacyPolicyPage;
